/**
 * Hooks the Top performing content widget (and any other surface) uses for
 * the post breakdown and Tune sentiment, without touching RTK Query details.
 */
import { skipToken } from '@reduxjs/toolkit/query';
import type { BreakdownView } from '@deepdive-ui';
import { useCallback, useMemo } from 'react';

import { useDataSource } from '../data/DataSourceContext';
import {
  useGetPostBreakdownQuery,
  useSaveSentimentOverridesMutation,
} from './api/conversations.api';
import { presentBreakdown } from './present';

/** One post's breakdown view, or undefined while `postId` is null or loading. */
export function usePostBreakdown(
  postId: number | null,
  noun: 'post' | 'conversation' = 'post',
): BreakdownView | undefined {
  const { source, bootstrap } = useDataSource();
  const { currentData } = useGetPostBreakdownQuery(
    postId === null ? skipToken : { source, params: { postId } },
  );
  return useMemo(
    () => (currentData ? presentBreakdown(currentData, bootstrap.filterOptions, noun) : undefined),
    [currentData, bootstrap.filterOptions, noun],
  );
}

/**
 * Saves Tune sentiment corrections (post id → sentiment). On success every
 * widget and conversations list of this source refetches with them applied.
 */
export function useSaveSentimentOverrides(): (changes: Readonly<Record<number, string>>) => void {
  const { source } = useDataSource();
  const [save] = useSaveSentimentOverridesMutation();
  return useCallback(
    (changes) => {
      const list = Object.entries(changes).map(([postId, sentiment]) => ({
        postId: Number(postId),
        sentiment,
      }));
      if (list.length > 0) void save({ source, params: { changes: list } });
    },
    [save, source],
  );
}
